import React, { useCallback, useSyncExternalStore } from 'react'
import { useSelector, useStore } from 'react-redux'
import { Alert, Button, Snackbar } from '@mui/material'

import {
  applyUpdate,
  dismissUpdate,
  getUpdateState,
  subscribeUpdate,
} from '../utils/updateAnnouncer'
import { evaluateUpdateGate } from '../utils/updateGate'
import { LocalStorageHelper } from '../utils/localStorageHelper'

/**
 * Aviso de "Nova versão disponível". A versão nova já está baixada; ela só entra quando o usuário
 * pede E o sync está parado (ver `utils/updateGate.ts`). Nada recarrega sozinho.
 *
 * Montado em `index.js` dentro do <Provider> (lê `state.sync.currentSync`), fora do router.
 */

const MESSAGES = {
  default:
    'Nova versão disponível. Salve o que estiver preenchendo antes de atualizar.',
  activated_elsewhere:
    'A versão nova foi ativada em outra aba. Recarregue esta aba para usá-la.',
}

export const UpdateAvailableBannerView = ({
  open,
  source,
  gate,
  applying,
  onApply,
  onDismiss,
}) => {
  if (!open) return null
  const message = MESSAGES[source] || MESSAGES.default
  const blocked = !gate.allowed
  return (
    <Snackbar
      open
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      data-testid='update-available-banner'
    >
      <Alert
        severity='info'
        variant='filled'
        role='status'
        action={
          <>
            <Button
              color='inherit'
              size='small'
              onClick={onDismiss}
              disabled={applying}
            >
              Depois
            </Button>
            <Button
              color='inherit'
              size='small'
              variant='outlined'
              onClick={onApply}
              disabled={blocked || applying}
              sx={{ ml: 1, fontWeight: 700 }}
            >
              {applying ? 'Atualizando...' : 'Atualizar agora'}
            </Button>
          </>
        }
      >
        {message}
        {blocked && !applying && (
          <>
            <br />
            <strong>{gate.message}</strong>
          </>
        )}
      </Alert>
    </Snackbar>
  )
}

const UpdateAvailableBanner = ({ reload }) => {
  const update = useSyncExternalStore(subscribeUpdate, getUpdateState)
  const currentSync = useSelector((state) => state.sync.currentSync)
  const store = useStore()

  const onApply = useCallback(() => {
    // Reavalia com o estado de AGORA (o último render pode estar desatualizado). Se o sync
    // estiver ocupado, `applyUpdate` não faz nada e o botão já mostra o motivo.
    void applyUpdate({
      getSyncStatus: () => store.getState().sync.currentSync,
      reload,
      // Depois da atualização o sidebar mostra "Você está agora na versão X" (updateVersion).
      beforeActivate: () => LocalStorageHelper.add('updateVersion', true),
      onTimeout: () => LocalStorageHelper.remove('updateVersion'),
    })
  }, [store, reload])

  return (
    <UpdateAvailableBannerView
      open={update.phase !== 'idle' && !update.dismissed}
      source={update.source}
      gate={evaluateUpdateGate(currentSync)}
      applying={update.phase === 'applying'}
      onApply={onApply}
      onDismiss={dismissUpdate}
    />
  )
}

export default UpdateAvailableBanner
